import type { PropsWithChildren, SyntheticEvent } from "react";
import { useEffect, useMemo } from "react";

import { Button, Modal } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import { isCompetitiveIntelligenceUser, isWincherUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { TRAFFIC_MIN_VALUE } from "single-spa/react-app/router/utils/paramsRouting/constants";
import { quizEmailToDomain } from "single-spa/react-app/router/utils/paramsRouting/utils";

import { useSessionStorage } from "../../hooks/helpers/useSessionStorage";

import { FIRST_LAND_PREF_KEY, MODAL_STYLES, type QuizSplitUseCaseGroup } from "./constants";
import { FirstLandModalStateProvider, useFirstLandModal } from "./context";
import * as SC from "./styles";
import {
    BTN_TEXT,
    getModalText,
    getWincherModalText,
    MESSAGE_1,
    MESSAGE_2,
    TITLE,
    TRACKING_VALUE,
    WINCHER_TITLE,
    WINCHER_TRACK_NAME,
} from "./texts";
import type { UseCaseConfig } from "./types";

const FirstLandModalUI = () => {
    const { getSessionStorage, removeSessionStorage } = useSessionStorage();
    const { isOpen, setOpen, close } = useFirstLandModal();

    const user = swSettings?.user;
    const customData = user?.customData;

    const name = (user?.firstname as string) || "";
    const quiz = (customData?.quiz?.UseCase as string) || "";
    const email = (user?.username as string) || "";

    const trafficValue = Number(customData?.quiz?.monthlyVisitsEnrichment);
    const hasMonthlyTraffic = trafficValue >= TRAFFIC_MIN_VALUE;

    const isBlocked = Boolean(customData?.IsDomainDbBlocked);

    const quizWebsite = customData?.quiz?.website as string | undefined;

    const isCCForced = Boolean(customData?.IsDomainCcForced);

    const key = isCCForced ? quizWebsite : quizEmailToDomain(email);
    const normalizedKey = key?.trim();
    const isCiPackage = isCompetitiveIntelligenceUser(swSettings);
    const isWincher = isWincherUser(swSettings);

    const isEnrichedFlow = hasMonthlyTraffic && !isBlocked && Boolean(normalizedKey) && !isCCForced;
    const trackName = isWincher
        ? WINCHER_TRACK_NAME
        : isEnrichedFlow
          ? "landing_usecase_modal_enriched"
          : "landing_usecase_modal";

    const texts = useMemo<UseCaseConfig>(
        () => (isWincher ? getWincherModalText() : getModalText(quiz as QuizSplitUseCaseGroup, isCiPackage)),
        [isWincher, quiz, isCiPackage],
    );

    useEffect(() => {
        const prefValue = PreferencesService.get(FIRST_LAND_PREF_KEY) as string | boolean | undefined;
        const sessionShouldShow = getSessionStorage(FIRST_LAND_PREF_KEY);
        const shouldShow = Boolean(sessionShouldShow) || Boolean(prefValue);

        if (shouldShow) {
            setOpen(true);

            allTrackers.dispatchTrackEvent(
                "popup",
                "Show",
                `${trackName}/${TRACKING_VALUE} ${texts.tracking1}/${texts.tracking2}`,
            );
        }

        return () => {
            PreferencesService.add({ [FIRST_LAND_PREF_KEY]: false });
            removeSessionStorage(FIRST_LAND_PREF_KEY);
        };
    }, [texts, trackName, getSessionStorage, removeSessionStorage, setOpen]);

    const onClose = async (event: SyntheticEvent<HTMLButtonElement>) => {
        event.preventDefault();

        await PreferencesService.add({ [FIRST_LAND_PREF_KEY]: false });
        removeSessionStorage(FIRST_LAND_PREF_KEY);

        close();

        allTrackers.dispatchTrackEvent(
            "popup",
            "click",
            `${trackName}/${TRACKING_VALUE} ${texts.tracking1}/${texts.tracking2}`,
        );
    };

    const stopBubbling = (event: SyntheticEvent<HTMLSpanElement>) => event.stopPropagation();

    return (
        <span onClick={stopBubbling}>
            <Modal
                isOpen={isOpen}
                customStyles={{ content: MODAL_STYLES }}
            >
                <SC.Container>
                    <SC.HookContainer>
                        <SC.CloseButton
                            iconName="close"
                            type="flat"
                            onClick={onClose}
                            iconSize="xs"
                            data-automation="first-land-modal-close-button"
                        />

                        <SC.Body>
                            <SC.Title $wincher={isWincher}>
                                {isWincher ? translate(WINCHER_TITLE) : translate(TITLE, { name })}
                            </SC.Title>

                            <SC.Message>
                                {texts.message ? (
                                    <>
                                        <SC.Line $wincher={isWincher}>{translate(texts.message)}</SC.Line>
                                        {texts.message2 ? (
                                            <SC.Line $wincher={isWincher}>{translate(texts.message2)}</SC.Line>
                                        ) : null}
                                    </>
                                ) : (
                                    <>
                                        <SC.Line>
                                            {translate(MESSAGE_1)}&nbsp;
                                            <SC.MessageBold>{translate(texts.message1Bold)}</SC.MessageBold>
                                        </SC.Line>
                                        <SC.Line>
                                            {translate(MESSAGE_2)}&nbsp;
                                            <SC.MessageBold>{translate(texts.message2Bold)}</SC.MessageBold>
                                        </SC.Line>
                                    </>
                                )}
                            </SC.Message>
                        </SC.Body>

                        {texts.image ? (
                            <SC.Illustration
                                src={AssetsService.assetUrl(texts.image)}
                                alt=""
                                data-automation="first-land-modal-illustration"
                            />
                        ) : null}

                        <SC.Footer>
                            <Button
                                type="primary"
                                onClick={onClose}
                            >
                                {translate(texts.ctaText || BTN_TEXT)}
                            </Button>
                        </SC.Footer>
                    </SC.HookContainer>
                </SC.Container>
            </Modal>
        </span>
    );
};

export const FirstLandModalProvider = ({ children }: PropsWithChildren) => {
    return (
        <FirstLandModalStateProvider>
            {children}
            <FirstLandModalUI />
        </FirstLandModalStateProvider>
    );
};
